import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { uuidv7 } from '@stem/lab-engine';
import type { IBlock, ICourseTree, IEnrollment, ILesson, IProgressEvent } from '@stem/contracts';
import { apiClient, ApiError } from '../services/api';
import { offlineQueue } from '../offline/queue';
import { BlockRenderer, type BlockAnswer } from './blocks';
import { UpgradePrompt } from '../billing/UpgradePrompt';

interface LessonContent {
  lesson: ILesson;
  version: number;
  blocks: IBlock[];
  published: boolean;
}

/**
 * The course player.
 *
 * Everything a learner does here becomes a `progress_event` written to
 * IndexedDB first and sent second — always, not only when the browser thinks it
 * is offline. A queue that engages on `navigator.onLine === false` still loses
 * the event that was in flight when the signal dropped, and on a rural 2G
 * connection that is most of them.
 */
export function LessonPage() {
  const { orgSlug = '', slug = '', lessonId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [answers, setAnswers] = useState<Record<string, BlockAnswer>>({});
  const [completed, setCompleted] = useState(false);

  const course = useQuery({
    queryKey: ['course', slug],
    queryFn: () => apiClient.get<ICourseTree>(`/v1/catalog/courses/${slug}`),
  });

  const content = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: () => apiClient.get<LessonContent>(`/v1/catalog/lessons/${lessonId}`),
    retry: false,
  });

  const enrollments = useQuery({
    queryKey: ['enrollments'],
    queryFn: () => apiClient.get<IEnrollment[]>('/v1/learn/enrollments'),
  });

  const enrollment = enrollments.data?.find(row => row.courseId === course.data?.id);

  const flat = useMemo(() => {
    const lessons = course.data?.modules.flatMap(module => module.lessons) ?? [];
    const index = lessons.findIndex(lesson => lesson.id === lessonId);
    return { lessons, index, previous: lessons[index - 1], next: lessons[index + 1] };
  }, [course.data, lessonId]);

  const record = useCallback(
    (event: Omit<IProgressEvent, 'id' | 'enrollmentId' | 'occurredAt'>) => {
      if (!enrollment) return;
      void offlineQueue.enqueue({
        ...event,
        id: uuidv7(),
        enrollmentId: enrollment.id,
        occurredAt: new Date().toISOString(),
      } as IProgressEvent);
    },
    [enrollment],
  );

  const onAnswered = useCallback(
    (answer: BlockAnswer) => {
      setAnswers(current => ({ ...current, [answer.blockId]: answer }));
      record({
        type: 'block_answered',
        lessonId,
        blockId: answer.blockId,
        score: answer.score,
        maxScore: answer.maxScore,
        correct: answer.correct,
      });
    },
    [lessonId, record],
  );

  const complete = useCallback(async () => {
    record({ type: 'lesson_completed', lessonId });
    setCompleted(true);
    // Optimistic: the queue may not have flushed yet, and the learner should
    // see their progress bar move whether or not the network cooperated.
    await queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    if (flat.next) navigate(`/o/${orgSlug}/courses/${slug}/lessons/${flat.next.id}`);
  }, [record, lessonId, queryClient, flat.next, navigate, orgSlug, slug]);

  if (content.isLoading) return <p className="text-sm text-ink-500">Loading lesson…</p>;

  if (content.error) {
    const error = content.error as ApiError;
    if (error.needsUpgrade) {
      return (
        <UpgradePrompt
          title="This lesson is part of a premium membership"
          message={error.message}
          backTo={`/o/${orgSlug}/courses/${slug}`}
        />
      );
    }
    return <p className="text-sm text-red-700">{error.message}</p>;
  }

  if (!content.data) return null;
  const lesson = content.data;

  return (
    <div className="mx-auto max-w-3xl">
      <nav className="mb-4 text-xs text-ink-500">
        <Link to={`/o/${orgSlug}/courses/${slug}`} className="hover:underline">
          {course.data?.title ?? 'Course'}
        </Link>
        <span className="mx-1">/</span>
        <span>
          Lesson {flat.index + 1} of {flat.lessons.length}
        </span>
      </nav>

      <h1 className="text-2xl font-bold text-ink-900">{lesson.lesson.title}</h1>
      {!lesson.published && (
        <p className="mt-1 text-xs text-savanna-600">
          You are reading an unpublished draft (version {lesson.version}).
        </p>
      )}

      {!enrollment && (
        <p className="mt-3 rounded-lg bg-savanna-400/15 p-3 text-sm text-savanna-600">
          You are not enrolled on this course, so your progress is not being recorded.{' '}
          <Link to={`/o/${orgSlug}/courses/${slug}`} className="underline">
            Enrol
          </Link>
          .
        </p>
      )}

      <div className="mt-6 space-y-5">
        {lesson.blocks.map(block => (
          <BlockRenderer
            key={block.id}
            block={block}
            onAnswered={onAnswered}
            answered={answers[block.id]}
          />
        ))}
      </div>

      <footer className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-ink-200 pt-5">
        {flat.previous ? (
          <Link
            to={`/o/${orgSlug}/courses/${slug}/lessons/${flat.previous.id}`}
            className="btn-secondary"
          >
            ← {flat.previous.title}
          </Link>
        ) : (
          <span />
        )}

        <button type="button" className="btn-primary" onClick={() => void complete()}>
          {completed ? 'Completed' : flat.next ? 'Mark complete and continue' : 'Mark complete'}
        </button>
      </footer>
    </div>
  );
}
